import { parse } from 'query-string'

import {
  captureErrorOpeningWindowFromEmbedEvent,
  capturePostMessageWarning,
  captureWarningForBackupRedirect,
} from 'embed/constants/errors'

import { brandFromHost } from 'embed/constants/config'
import {
  EVEN_REQUEST_REDIRECT_EVENT,
  EVEN_VIEWPORT_UPDATE_EVENT,
  TargetTypes,
  ViewportUpdateMessageBody,
} from 'embed/constants'

import { getBrandObject, originFromUrl } from 'embed/utils'
import {
  Brand,
  CreditCardStep,
  DepositsStep,
  EmbedUpdateStepMessageBody,
  EVEN_EMBED_UPDATE_STEP_EVENT,
  EVEN_HELPER_EVENT,
  EVEN_IFRAME_INIT_EVENT,
  EVEN_MESSAGE_PREFIX,
  EVEN_SCROLL_EVENT,
  EVEN_URL_APPEND_EVENT,
  EVEN_URL_PARAM_REMOVE_EVENT,
  FIONA_MESSAGE_PREFIX,
  LEAPLIFE_MESSAGE_PREFIX,
  LifeInsuranceStep,
  LoanStep,
  ParentData,
  ParentDataKeys,
  Product,
} from 'even-ts-static'
interface ParentContext {
  url: string
  referrer?: string
}

interface ParentMessage {
  type: string
  body?: string | object | undefined
}
interface Message {
  data: ParentMessage
}

const UNSAFE_ORIGIN = '*'
const INIT_TIMEOUT = 8000
const RETRY_DELAY = 1000

const isNode = () => typeof process !== 'undefined'

const stepMap = {
  [Product.CreditCard]: CreditCardStep,
  [Product.Deposits]: DepositsStep,
  [Product.LifeInsurance]: LifeInsuranceStep,
  [Product.Loan]: LoanStep,
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isEmbedUpdateStepMessageBody = (body: any): body is EmbedUpdateStepMessageBody =>
  typeof body === 'object' &&
  body !== null &&
  'product' in body &&
  Object.values(Product).includes(body.product) &&
  'step' in body &&
  Object.values(stepMap[body.product]).includes(body.step)

// In no circumstances should any ParentData or message be rendered to HTML
export class Parent {
  viewportSubs: ((data: ViewportUpdateMessageBody) => void)[] = []
  embedStepSubs: ((data: EmbedUpdateStepMessageBody) => void)[] = []
  private readonly window: Window
  private readonly context: Promise<ParentContext>
  private readonly brand?: Brand
  private data: Promise<ParentData>
  private retry = true
  private connected = false

  constructor(window: Window, brand?: Brand) {
    this.window = window
    this.brand = brand ?? brandFromHost
    this.context = new Promise<ParentContext>((resolve, reject) => {
      const timeout = this.window.setTimeout(() => {
        capturePostMessageWarning(`No Helper found in ${INIT_TIMEOUT}ms`)
      }, INIT_TIMEOUT)

      /**
       * If you need to access msg.data.body in this event listener callback
       * make sure to deep clone it with JSON.parse(JSON.stringify(msg.data.body)) first.
       * Otherwise, there's a high likelihood of duplicate events happening.
       * See MPS-13.
       */
      this.window.addEventListener('message', (msg: Message) => {
        if (msg.data.type === EVEN_HELPER_EVENT) {
          this.retry = false
          this.connected = true
          clearTimeout(timeout)
          resolve((msg as unknown) as ParentContext)
        } else if (msg.data.type === EVEN_EMBED_UPDATE_STEP_EVENT) {
          this.handleEmbedStepUpdate(msg)
        } else if (msg.data.type === EVEN_VIEWPORT_UPDATE_EVENT) {
          this.handleViewportUpdate(msg)
        } else if (msg.data.type === EVEN_REQUEST_REDIRECT_EVENT) {
          const body = JSON.parse(JSON.stringify(msg.data.body))
          captureWarningForBackupRedirect()
          if (window.open(body.url, TargetTypes.CURRENT_WINDOW) === undefined) {
            // Force setting current window in this extreme case
            captureErrorOpeningWindowFromEmbedEvent(body.url)
          }
        }
      })
    }).then((msg) => this.parseBody(msg))

    this.tryInit()
  }

  isParentAttached() {
    return this.connected
  }

  subscribeToEmbedStepUpdates(callback: (data: EmbedUpdateStepMessageBody) => void) {
    this.embedStepSubs.push(callback)
  }

  subscribeToViewportUpdates(callback) {
    this.viewportSubs.push(callback)
  }

  async getUrl(): Promise<string> {
    return this.context.then((data) => data.url)
  }

  getData = async (): Promise<ParentData> => {
    this.data = this.context.then((context) => {
      const params = this.getUrlParams(context.url)
      return { ...params, referrer: context.referrer, url: context.url }
    })
    return this.data
  }

  updateUrl(paramKey: string, paramValue: string) {
    this.postMessage({
      body: {
        key: paramKey,
        value: paramValue,
      },
      type: EVEN_URL_APPEND_EVENT,
    })
  }

  removeFromUrl(paramKey: string) {
    this.postMessage({
      body: {
        key: paramKey,
      },
      type: EVEN_URL_PARAM_REMOVE_EVENT,
    })
  }

  requestRedirect(url: string, target: TargetTypes) {
    if (this.isParentAttached()) {
      this.postMessage({
        body: {
          target,
          url,
        },
        type: EVEN_REQUEST_REDIRECT_EVENT,
      })
    } else {
      if (window.open(url, target) === undefined) {
        captureErrorOpeningWindowFromEmbedEvent(url)
      }
    }
  }

  scrollToFrame = (additionalOffset = 0) => {
    this.postMessage({
      body: {
        additionalOffset,
      },
      type: EVEN_SCROLL_EVENT,
    })
  }

  publishMessage(type: string, body?: object) {
    if (!type.startsWith(EVEN_MESSAGE_PREFIX)) {
      const prependedType = this.brand ? `${getBrandObject(this.brand).messagePrefix}${type}` : ''
      this.postMessage({ body, type: prependedType })
    } else {
      this.postMessage({ body, type })
    }
  }

  private tryInit() {
    // Alert parent that iframe is ready
    try {
      this.unsafePostMessage({
        type: EVEN_IFRAME_INIT_EVENT,
      })
    } catch (e) {
      this.retry = false
      this.connected = false
      capturePostMessageWarning('Parent attached to missing iframe')
      // If an iframe is being destroyed before Parent can attach to host
      // We would like visibility into that
    }

    // Retry in X seconds
    this.window.setTimeout(() => {
      if (this.retry && !isNode()) {
        this.tryInit()
      }
    }, RETRY_DELAY)
  }

  private async parseBody(msg): Promise<ParentContext> {
    let body
    try {
      body = JSON.parse(msg.data.body)
    } catch (e) {
      return Promise.reject('Parent message data did not include a body')
    }

    if (body.url) {
      return Promise.resolve(body)
    } else {
      return Promise.reject('Parent message body not complete')
    }
  }

  private handleEmbedStepUpdate(msg: Message) {
    const {
      data: { body },
    } = msg
    if (isEmbedUpdateStepMessageBody(body)) {
      this.embedStepSubs.forEach((sub) => sub(body))
    }
  }

  private handleViewportUpdate(msg) {
    let viewportUpdateData: ViewportUpdateMessageBody

    try {
      viewportUpdateData = JSON.parse(msg.data.body)
    } catch (e) {
      return // TODO
    }

    this.viewportSubs.forEach((sub) => {
      sub(viewportUpdateData)
    })
  }

  private readonly getUrlParams = (url: string): ParentData => {
    const searchIndex = url.indexOf('?')
    const parentParams: ParentData = {}
    if (searchIndex !== -1) {
      const params = parse(url.slice(searchIndex, url.length))
      Object.keys(params).forEach((key) => {
        Object.values(ParentDataKeys).forEach((keyName) => {
          // Only check for keys prefixed with even_ or fiona_
          if (
            new RegExp(`(${EVEN_MESSAGE_PREFIX}|${FIONA_MESSAGE_PREFIX}|${LEAPLIFE_MESSAGE_PREFIX})${keyName}`).test(
              key,
            )
          ) {
            parentParams[keyName] = params[key]
          }
        })
      })
    }
    return parentParams
  }

  private async getPublicOrigin(): Promise<string> {
    return this.getUrl().then(originFromUrl)
  }

  private unsafePostMessage(message: ParentMessage) {
    this.window.parent.postMessage(
      {
        type: message.type,
      },
      UNSAFE_ORIGIN,
    )
  }

  private postMessage(message: ParentMessage) {
    this.getPublicOrigin()
      .then((publicOrigin) => {
        this.window.parent.postMessage(
          {
            body: JSON.stringify(message.body),
            type: message.type,
          },
          publicOrigin,
        )
      })
      .catch((e) => {
        capturePostMessageWarning(e)
      })
  }
}

export default new Parent(window)
